/*  RHFRadioGroup.tsx
    ⟵ copy–paste this file, then replace your old import */
import type { RadioProps } from '@mui/material/Radio';
import type { Theme, SxProps } from '@mui/material/styles';
import type { FormLabelProps } from '@mui/material/FormLabel';
import type { RadioGroupProps } from '@mui/material/RadioGroup';
import type { FormHelperTextProps } from '@mui/material/FormHelperText';

import { Controller, useFormContext } from 'react-hook-form';

import Radio from '@mui/material/Radio';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';

type Primitive = string | number | boolean | null | undefined;

type Props = RadioGroupProps & {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  /** `"string" | "number" | "boolean"`  – omit to auto-detect */
  valueType?: 'string' | 'number' | 'boolean';
  slotProps?: {
    wrap?: SxProps<Theme>;
    radio: RadioProps;
    formLabel: FormLabelProps;
    formHelperText: FormHelperTextProps;
  };
  options: { label: string; value: Primitive }[];
  disabled?: boolean;
};

export function RHFRadioGroup({
  name,
  label,
  options,
  helperText,
  slotProps,
  disabled,
  valueType,
  ...other
}: Props) {
  const { control } = useFormContext();

  const type = valueType ?? (options.length ? typeof options[0].value : 'string');

  const cast = (raw: string): Primitive =>
    type === 'number' ? Number(raw) : type === 'boolean' ? raw === 'true' : raw;

  const labelId = `${name}-label`;
  const aria = (txt: string) => `Radio ${txt}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { value, onChange, ...rest } = field;

        return (
          <FormControl component="fieldset" sx={slotProps?.wrap}>
            {label && (
              <FormLabel
                id={labelId}
                component="legend"
                {...slotProps?.formLabel}
                sx={{ mb: 1, typography: 'body2', ...slotProps?.formLabel?.sx }}
              >
                {label}
              </FormLabel>
            )}

            <RadioGroup
              {...rest}
              {...other}
              aria-labelledby={labelId}
              value={value == null ? '' : String(value)} // DOM needs string
              onChange={(e) => onChange(cast(e.target.value))} // string ➜ typed value
            >
              {options.map((o) => (
                <FormControlLabel
                  key={String(o.value)}
                  value={String(o.value)} // DOM string
                  disabled={disabled}
                  control={
                    <Radio
                      {...slotProps?.radio}
                      inputProps={{
                        ...(!o.label && { 'aria-label': aria(String(o.label)) }),
                        ...slotProps?.radio?.inputProps,
                      }}
                    />
                  }
                  label={o.label}
                />
              ))}
            </RadioGroup>

            {(error || helperText) && (
              <FormHelperText error={!!error} sx={{ mx: 0 }} {...slotProps?.formHelperText}>
                {error ? error.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
